import podcast_genre from '../images/results/podcast_genre.gif'
import podcast_tone from '../images/results/podcast_tone.gif'
import hosting_style from '../images/results/hosting_style.gif'
import publishing_schedule from '../images/results/publishing_schedule.gif'

import hosting_style__co_host from '../images/results/hosting_style/co-host.png'
import hosting_style__collaborative from '../images/results/hosting_style/collaborative.png'
import hosting_style__minimal from '../images/results/hosting_style/minimal.png'
import hosting_style__solo from '../images/results/hosting_style/solo.png'
import hosting_style__other from '../images/results/hosting_style/other.png'

import podcast_genre__community from '../images/results/podcast_genre/community.png'
import podcast_genre__current_affairs from '../images/results/podcast_genre/current_affairs.png'
import podcast_genre__educational from '../images/results/podcast_genre/educational.png'
import podcast_genre__entertainment from '../images/results/podcast_genre/entertainment.png'
import podcast_genre__experiential from '../images/results/podcast_genre/experiential.png'
import podcast_genre__instructional from '../images/results/podcast_genre/instructional.png'
import podcast_genre__memoir from '../images/results/podcast_genre/memoir.png'
import podcast_genre__other from '../images/results/podcast_genre/other.png'

import publishing_schedule__seasonal_non_fluid from '../images/results/publishing_schedule/seasonal_non_fluid.png'
import publishing_schedule__seasonal_fluid from '../images/results/publishing_schedule/seasonal_fluid.png'
import publishing_schedule__infinite from '../images/results/publishing_schedule/infinite.png'
import publishing_schedule__finite from '../images/results/publishing_schedule/finite.png'

type HiddenCategory = {
  type: 'hidden'
  features: {
    [feature: string]: undefined
  }
}

//TODO make the features autocomplete possible
type DefaultCategory = {
  type: 'default' | 'show_images'
  cover_image: string
  /**
   * defaults to square
   */
  feature_image_type?: 'circle' | 'square'
  title?: string
  post_script?: string
  features: {
    [feature: string]: FeatureDetail
  }
}

export const isHiddenCategory = (
  category: HiddenCategory | DefaultCategory,
): category is HiddenCategory => {
  if (category.type === 'hidden') return true
  return false
}

export const category_info: {
  [category: string]: HiddenCategory | DefaultCategory
} = {
  fiction_nonfiction: {
    type: 'hidden',
    features: {
      fiction: undefined,
      nonfiction: undefined,
    },
  },
  podcast_genre: {
    type: 'default',
    cover_image: podcast_genre,
    features: {
      instructional: {
        image: podcast_genre__instructional,
        header: 'When people listen they get instant, tangible results.',
        body:
          'Your podcast isn’t just here to teach about a certain topic – no! Your show literally walks people through a journey or process to help them achieve a tangible result.',
        note:
          'Make sure to test your content out and ensure your listeners are achieving the result you’re promoting. Cut to the chase, minimize the fluff, and deliver the goods.',
        type: 'default',
      },
      educational: {
        image: podcast_genre__educational,
        header: 'When people listen, they learn (a lot!).',
        body:
          'Your show shares knowledge and wisdom that helps your listeners elevate their skills or insights about a certain topic. Your show is here to serve a community of listeners who are eager to learn from you.',
        note:
          'Be clear on what your listeners can expect to learn when tuning in, and make sure you deliver the goods! ',
        type: 'default',
      },
      current_affairs: {
        image: podcast_genre__current_affairs,
        header: 'You’ve got your thumb on the pulse of your topic.',
        body:
          'Your show is here to keep your community current on all things XYZ!  Whether that thing is sports, politics, or the latest blockbuster, your show is the place to go to receive all the latest and most helpful insights about that one obsession.',
        note:
          'Embrace your inner addict and go all-in on this one thing. Treat your show like an ultimate clubhouse for people who share your same obsession and where you’re the go-to person for all the latest news and insights.',
        type: 'default',
      },
      entertainment_fiction: {
        requires: 'fiction_nonfiction>fiction',
        title: 'Entertainment',
        image: podcast_genre__entertainment,
        header:
          'You paint an entire world for your listeners through the magic of audio and storytelling. ',
        body:
          'Your show is first and foremost here to entertain. Your concept, story, and audio execution allow listeners to temporarily escape to a world of pure imagination, resulting in who knows what! Perhaps they’ll learn a moral lesson, empathize with complex characters, or simply laugh their butts off –– whatever the outcome, your show will, at the least, keep your listeners engaged.',
        note:
          'Don’t hesitate to go the extra mile when it comes to sound quality, editing, acting, and other production elements in order to paint an audio world your listeners can truly escape to.',
        type: 'default',
      },
      entertainment_nonfiction: {
        requires: 'fiction_nonfiction>nonfiction',
        title: 'Entertainment',
        image: podcast_genre__entertainment,
        header:
          'You create an engaging world for your listeners through the magic of audio and storytelling that’s based on real life.',
        body:
          'Your show isn’t just here to deliver the facts –– it’s here to entertain! Your subject matter, combined with audio execution allows listeners to temporarily escape to a world, resulting in who knows what! Perhaps they’ll learn a new insight, explore other cultures,  or simply laugh their butts off –– whatever the outcome, your show will, at the least, keep your listeners engaged.',
        note:
          'Real life doesn’t have to be boring! Embrace your inner showman a bit, and ask yourself how the topic and content of your show can be curated to create an audio experience your listeners will enjoy.',
        type: 'default',
      },
      experiential: {
        image: podcast_genre__experiential,
        header:
          'Talking is not the focal point, it’s all about the experience. When people listen they are often affected on a somatic level.',
        body:
          'Forget the small talk! Your podcast goes beyond chit-chat and truly allows your listeners to have a mental and/or physical encounter through audio. Experiential shows are like the psychedelics of the podcast world. They think outside the box, go deep, and push boundaries.',
        note:
          'Make sure you are paying extra special attention to the audio quality of your show. Effective experiential podcasts never cut corners on production value and often even spearhead new ways of utilizing cutting-edge audio and production techniques that raise the bar on audio quality in the industry. Invest the extra time and resources into proper high-quality audio equipment.',
        type: 'default',
      },
      memoir: {
        image: podcast_genre__memoir,
        header:
          'When people listen they learn about a real person, event, community, or special moment in time.',
        body:
          'Your podcast is largely about discovery. Whether you are uncovering a complicated person’s life experiences or exploring the ins and outs of complex historical events, your listeners feel like they are right there by your side throughout the journey.',
        note:
          'Embrace your inner storyteller when creating your podcast. Capture your subject matter in various ways, such as through interviews of various people, stock footage, sound effects, music, and other production elements that help express your story in a captivating way. It is often helpful when creating a memoir-style podcast to center the final production around a theme or central concept. What’s the thread that stays woven throughout the story that allows for a fluid experience for the listener?',
        type: 'default',
      },
      community: {
        image: podcast_genre__community,
        header:
          'Your show exists for a community of people who, when they tune in, feel like they’re home.',
        body:
          'From horse lovers to stay-at-home moms, there seems to be a podcast for everyone. As humans, we experience life through the lens of several different identities. Your podcast creates a home for people who, like you, bond over a certain identity, interest, or lifestyle.',
        note:
          'It’s important when creating a podcast built around a certain community, that you don’t make the show all about YOU. When creating content, ask yourself, “does this serve my community?” If not, how can you include your community into the conversation more and become a helpful resource that helps more than just yourself?',
        type: 'default',
      },
      other_combination: {
        image: podcast_genre__other,
        header:
          'Your show can’t be fit into a box or it combines elements from multiple genres.',
        body:
          "Podcasting is still a relatively new frontier. Compared to industries like film, theater, or music which have been around for a century or more, podcasting as a medium has scarcely just begun. That means that when it comes to the kind of podcast you can create, the sky's the limit! Perhaps your show will follow a salon-style approach or combine several types of genres over multiple segments. More power to ya!",
        note:
          'Don’t feel like you have to fit into one category. If you have an idea that’s unique, hard to define or just straight-out bizarre, go for it!  Embrace your inner pioneer and show the world what podcasting can sound like in new and exciting ways.',
        type: 'default',
      },
    },
  },
  hosting_style: {
    type: 'default',
    cover_image: hosting_style,
    features: {
      solo_host_nonfiction: {
        requires: 'fiction_nonfiction>nonfiction',
        title: 'Solo Host',
        image: hosting_style__solo,
        header:
          'There is a clear main host for your show. That host drives the conversation and usually acts as the face of the brand.',
        body:
          'Solo-hosted shows are one of the most popular hosting styles found in podcasting. Whether the host conducts interviews, uses multiple sound bite sources, or does most of the talking themselves, the host’s voice and personality are the thread that weaves throughout every episode.  Solo hosts are often producers of their own show too but not always. For long-running shows, there may be other contributors or producers who take over the mic now and then as well, but usually, in this instance, the main host will still handle introductions and create consistency throughout the podcast.',
        note:
          'Being a great solo-host is all about showing your authentic personality, and in the case of interviewing, it’s also about being a great listener. Check out some of these solo-hosted podcasts. Learn from them, and let them inspire you for your own show. Pay special attention to their vocal technique, talking styles, authenticity, and interviewing skills:',
        examples: [
          'Criminal (hosted by Phoebe Judge)',
          'The Life Coach School Podcast (hosted by Brooke Castillo)',
          'The Moth Radio Hour (hosted by Suzanne Rust)',
          'This American Life (hosted by Ira Glass)',
          'Super Soul (hosted by Oprah)',
          'On Being (hosted by Krista Tippett)',
        ],
        type: 'default',
      },
      solo_host_fiction: {
        requires: 'fiction_nonfiction>fiction',
        title: 'Solo Host',
        image: hosting_style__solo,
        header:
          'There is a clear main host for your show. That host drives the conversation and usually acts as the face of the brand.',
        body:
          "Solo-hosted shows are one of the most popular hosting styles found in podcasting. Whether the host narrates their own stories, other authors' work, or introduces other narrators, the host’s voice and personality are the thread that weaves throughout every episode.  Solo hosts are often producers of their show but not always. For long-running shows, there may be other contributors or producers who take over the mic now and then as well, but usually, in this instance, the main host will still handle introductions and create consistency throughout the podcast.",
        note:
          'Being a great solo-host is all about having a great voice and setting the perfect tone for the story. Check out some of these solo-hosted podcasts. Learn from them, and let them inspire you for your own show. Pay special attention to their vocal technique, talking styles, authenticity, and storytelling skills:',
        examples: [
          'Levar Burton Reads',
          'The No Sleep Podcast',
          'Spooked',
          'If I Go Missing, The Witches Did It',
        ],
        type: 'default',
      },
      cohost: {
        image: hosting_style__co_host,
        header:
          'Two heads are better than one! In a co-hosted podcast there are TWO clear main hosts for your show. That duo dynamic sets the tone, drives the conversation, and usually acts as the face of the brand.',
        body:
          'Co-hosted shows are a very popular hosting style, and for good reason. The right mash-up of personalities can act as a playground for clever banter, emotional insights, and dynamic tempo.',
        note:
          'Pulling off a great co-hosted podcast requires a number of factors that are important to take note of. For one, co-hosts should have a solid relationship with one another. Whether they are great friends, worst enemies, or offer two very different sets of skills or opinions, the best co-host duos usually balance each other out or exaggerate some sort of opposite. Great co-hosts also allow each other to speak without too many interruptions and play off one another’s words in a way that makes the listener laugh and enjoy. Above all, co-hosts act as a solid team, striving for one cohesive listening experience for the listener versus falling prey to a show that feels like you’re listening to two different solo hosts smashed together on one podcast.\n\nCheck out some of these co-hosted podcasts. Learn from them, and let them inspire you for your own show. Pay special attention to the ways they share the spotlight, play off one another, and highlight each other’s strengths:',
        examples: [
          'Back Issue',
          'Radiolab (pre 2020)',
          'The Nod',
          'Only the Rich',
          'Two Dope Queens',
        ],
        type: 'default',
      },
      collaborative_fiction: {
        requires: 'fiction_nonfiction>fiction',
        title: 'Collaborative',
        image: hosting_style__collaborative,
        header:
          'Collaboratively-hosted podcasts are hard to define because there are so many ways in which they can take shape. Sometimes there are one or two “main hosts” who introduce different collaborators who then take over the narration/hosting duties for that episode. Or there may be no clear host, but instead a whole group of speakers who make up the content of the show.',
        body: '',
        note:
          'Don’t be afraid to think outside the box if you’re planning to do a collaborative-style show –– there’s no ‘one right way’ to pull it off. But keep in mind, your audience will have little patience for a haphazard sounding podcast that has no clear direction or common theme to hold episodes together. Oftentimes, even when shows are collaboratively made, there tends to be one main narrator who steers the ship and creates structure for the show, doing intros, ad reads, etc. But with a collaborative-style show, those duties may change up from episode to episode or season to season. There is a difference between a host and an actor. Keep this in mind when creating your podcast. Think of the host as the face of your show/branding, while actors help create the content or make the producer’s vision come to life. Actors are interchangeable and don’t directly impact the branding, but are harder to untangle from the overall brand.\n\nCheck out some of these collaboratively-hosted podcasts. Learn from them, and let them inspire you for your own show. Pay special attention to the way the mic is shared and how continuity is still maintained from episode to episode: ',
        examples: [
          'Story Pirates',
          'Welcome to Nightvale',
          'Hello From the Magic Tavern',
        ],
        type: 'default',
      },
      collaborative_nonfiction: {
        requires: 'fiction_nonfiction>nonfiction',
        title: 'Collaborative',
        image: hosting_style__collaborative,
        header:
          'Collaboratively-hosted podcasts are hard to define because there are so many ways in which they can take shape. Sometimes there are one or two “main hosts” who introduce different collaborators who then take over the hosting duties for that episode. Or there may be no clear host, but instead a whole group of speakers who make up the content of the show.',
        body: '',
        note:
          'Don’t be afraid to think outside the box if you’re planning to do a collaborative-style show –– there’s no ‘one right way’ to pull it off. But keep in mind, your audience will have little patience for a haphazard sounding podcast that has no clear direction or common theme to hold episodes together. Often times, even when shows are collaboraativly made, there tends to be a main host who steers the ship and creates structure for the show. This allows listeners to follow along and enjoy more easily, but keep in mind, for a truly collaborative feel, these duties may want to get shared-out amongst collaborators from episode to episode. The golden rule for podcasting is to make the show enjoyable for LISTENERS, not just the host(s). So ask yourself, what does the audience get out of this? What’s in it for them? And make sure you aren’t only making a podcast with friends because it’s fun for you.\n\nCheck out some of these collaboratively-hosted podcasts. Learn from them, and let them inspire you for your own show. Pay special attention to the way the mic is shared and how continuity is still maintained from episode to episode:',
        examples: [
          'Midnight Miracle',
          'Pod Save America',
          'My Dad Wrote a Porno',
        ],
        type: 'default',
      },
      minimal: {
        image: hosting_style__minimal,
        header:
          'Minimally hosted shows are a rare yet fascinating style of podcasting. Oftentimes, hosts like to put themselves front and center of their show, creating a personal brand around “me, myself, and I,” but with minimally-hosted podcasts, it can sometimes be hard to even know who the host is! Instead, the podcast is almost entirely made of pure, undiluted content. ',
        body: '',
        note:
          'Don’t be afraid to think outside the box if you’re planning to do a minimally-hosted show –– there’s no ‘one right way’ to pull it off. However, to pull it off well, you need to have high-quality content front and center. The sound quality should be top-notch and well polished.\n\nThe best way to describe a minimally-hosted podcast is through example. Check out some of these minammly-hosted podcasts. Learn from them, and let them inspire you for your own show. Pay special attention to the way the episodes begin. They tend to drop the listener right in the middle of the content. Also, take note of how the high-quality audio stays front and center. Some of these podcasts rely on the episode description section within podcast apps to provide more context and information to the listeners rather than take up valuable audio space in the episode itself:',
        examples: [
          'Love + Radio',
          'Wolverine: The Long Night',
          'Sounds of Disney',
          'Blackout',
          'Limetown',
        ],
        type: 'default',
      },
      other: {
        image: hosting_style__other,
        header:
          'What can you do? Sometimes a podcast idea is so unique, it just doesn’t fit into ANY set hosting style. You’re a pioneer, and you’ll be doing things in ways we can’t even imagine! Good for you!',
        body: '',
        note:
          'If you feel like you’ve been given this answer in error, or you feel you could learn from seeing examples of other types of podcast hosting, then check out our list of all podcast hosting styles. Scroll through the other podcast hosting styles above to learn about the vaiuous options available to you.',
        type: 'default',
      },
    },
  },
  publishing_schedule: {
    type: 'default',
    cover_image: publishing_schedule,
    post_script:
      'P.S. Many people wonder how often they should release new content and how long their episodes should be. The answer to this is simple...do what your audience would enjoy most balanced with what you yourself are capable of maintaining. For example, Dan Carlin’s Hardcore History (one of the most beloved podcast of all time) only releases new episdoes about twice a year, but each episode is 3-6 hours long. On the other side of the spectrum you have a show like Your Stars Today, a daily horoscope podcast that produces 6-7 min long episodes every single day. Ask yourself first what you are capable of producing and at what caliber (quality-wise). Then ask yourself what is a reasonable schedule at which your audience would expect that content from you.',
    features: {
      finite: {
        image: publishing_schedule__finite,
        header:
          'Some things don’t need to get dragged out. Many interesting topics or stories have clear beginnings and endings and are best presented that way.  A finite podcast gets published, and then it’s over, never to return or get added onto again (minus the rare “bonus/update” episode now and then).',
        body:
          'One obvious advantage to producing a finite podcast is that you don’t have to keep coming up with new content for it. You can pour lots of resources into creating one really great show, rather than stretching yourself or your resources out too thin over time.  It’s a great way to dip your toe into podcasting or share a passion project of yours, without having to dedicate your entire life to podcasting.\n\nThe disadvantage to a finite podcast is that, unless your show becomes the next podcast blockbuster hit, it will be harder to market and monetize your show in the long run.\n\nThere are many shows that start out as a finite podcast but then evolve into a seasonal show over time, such as the wildly popular Serial, Dr. Death, and Up and Vanished. This often happens when the original series becomes such a big hit that the host decides to continue using that same title/brand to create new content. Or sometimes the success of a well-done finite series can help raise money, awareness, or prestige for the host, allowing them to then raise funds for even bigger projects.',
        note:
          'There are tons of outstanding and successful podcasts that are finite. Check out some of these finite shows. Learn from them, and let them inspire you for your own podcast. Notice how each show has an arc to the overall series, much like a TV show or novel where characters and plot developments are revealed over time, and suspense is maintained between episodes or chapters:',
        examples: [
          'Dirty John',
          'Dolly Parton’s America',
          'The Shrink Next Door',
          'Chasing Crosby',
          'The Habitat',
          'My Year in Mensa',
        ],
        type: 'default',
      },
      ongoing: {
        image: publishing_schedule__infinite,
        header:
          'Do all good things really come to an end? Not with ongoing podcasts! (Well, at least not for a very long time). Ongoing podcasts are ones that could seemingly continue forever. The host and general topic and style of the show tend to stay the same episode after episode, year after year.',
        body:
          'Whether your show produces new content every day, every week, every month, or somewhere in -between, there’s no clear stop date until you say so. And in some cases, shows continue on for longer by changing over the host (like when the host of a well-known TV show changes hands).\n\nThe key to a successful ongoing podcast is consistency! Stay as consistent as possible with your formatting, style, length, and publishing schedule.  Don’t publish new episodes once a week and then all of a sudden switch to once a month. Don’t play your intro music at the beginning of each episode for years, and then all of a sudden move it to the end. Whether someone tunes in for episode 1, or episode 300, they should be able to tell that it’s the same show. That’s not to say your show can’t evolve with time, but make sure the essence stays consistent over the years.',
        note:
          'Check out some of these famous ongoing shows. Learn from them, and let them inspire you for your own podcast. Notice how each episode has a consistent style. What things stay the same or change if you tune into an early episode vs. a newer one?:',
        examples: [
          'This American Life (First aired in 1995)',
          'Radiolab (First aired 2002)',
          'Hardcore History (First aired 2006)',
          'Entrepreneurs On Fire (First aired in 2012)',
          'Criminal (First aired in 2014)',
        ],
        type: 'default',
      },
      seasonal_fluid: {
        image: publishing_schedule__seasonal_fluid,
        header:
          'Seasonal Fluid podcasts operate similarly to how seasonal television shows operate: content is released (sometimes all at once, sometimes on a weekly basis), and then everything takes a break. The wait in-between can leave millions of fans in anguish waiting for the next season to arrive.',
        body:
          'This structure allows content creators to focus their attention and resources to a finite number of episodes per season, then take a much-needed break for other life demands, or simply to start work on another great season of content.\n\nIn order to maintain your fan-base in-between seasons, be sure to remind listeners to “Hit that follow button” on whatever app they use. Better still, direct your listeners to sign up for your e-newsletter so that they can be the first to know when your next season drops. Otherwise, it can be hard to rely on podcast apps alone to notify fans of a new season release.\n\nUnlike Seasonal Non-Fluid podcasts, Seasonal Fluid shows don’t change much from season to season. The same theme/story/topic is continued with little variation. Compare this to Seasonal Non-Fluid podcasts for further clarification.',
        note:
          'Check out some of these famous Seasonal Fluid shows. Learn from them, and let them inspire you for your own podcast. Notice how each season has a consistent theme or story.. What things stay the same or change if you tune in from season to season?:',
        examples: [
          'Revisionist History',
          'Back Issue',
          'Story Pirates',
          'The Two Princes',
        ],
        type: 'default',
      },
      seasonal_non_fluid: {
        image: publishing_schedule__seasonal_non_fluid,
        header:
          'Seasonal Non-Fluid podcasts are a clever approach to publishing that is almost entirely exclusive to the podcasting world. It’s when a podcast keeps its show title and podcast feed the same, but each season stands apart from the others as its own thing. For example, Serial (arguably the most famous podcast of all time),  has several seasons that live under the show title and podcast feed “Serial,” but each season covers an entirely different topic. Season 1 (their most famous) investigated the 1999 murder of Hae Min Lee (Korean: 이해민), and the investigation of her accused perpetrator, Adnan Masud Syed. After the wildly popular success of their first seasons, Serial released season 2 roughly two years later, which investigated the entirely unrelated story of Sergeant Bowe Bergdahl, an American Army soldier who was controversially tried for desertion after being held for five years by the Taliban. These two seasons (and subsequent seasons released by Serial, completely stand apart from one another. The only things tying them together are the show title, production team, reporter, and audio styling.',
        body:
          'This structure allows content creators to focus their attention and resources to a finite number of episodes per season, and fully explore one specific topic per season, giving it a distinct conclusion. It takes advantage of successful branding and show title recognition in order to take advantage of an established subscriber base to promote new content to, versus starting from scratch from a new podcast feed.\n\nIt also allows for lots of creativity for content producers as they can explore new ideas or stories from season to season. The podcast Dust does this beautifully, exploring entirely new concepts between seasons while maintaining their undeniably science-fiction roots.\n\nIn order to maintain your fan-base in-between seasons, be sure to remind listeners to “Hit that follow button” on whatever app they use. Better still, direct your listeners to sign up for your e-newsletter so that they can be the first to know when your next season drops. Otherwise, it can be hard to rely on podcast apps alone to notify fans of a new season release.\n\nCompare with Seasonal Non-Fluid podcasts for further clarification.  ',
        note:
          'Check out some of these famous Seasonal Non-Fluid shows. Learn from them, and let them inspire you for your own podcast. Notice how each season has a distinct concept. What things stay the same or change if you tune in from season to season?: ',
        examples: ['Serial', 'Dust', 'Dr. Death', 'Up and Vanished', 'Making'],
        type: 'default',
      },
    },
  },
  podcast_tone: {
    type: 'show_images',
    cover_image: podcast_tone,
    feature_image_type: 'circle',
    post_script:
      'Your cover art is the first impression new listeners experience of your podcast. Make sure your cover art reflects your tone loud and clear.  The following examples of cover art overlap your designated tone. Study the different design characteristics of these examples to inspire you when designing your own. Take note of colors, fonts, images, and other design elements to learn how well-designed cover art can create an impactful first-impression for your show.',
    features: {
      adult: { type: 'default' },
      casual: { type: 'default' },
      cheerful: { type: 'default' },
      clean: {},
      dark: {},
      disturbing: {},
      edgy: {},
      friendly: {},
      funny: {},
      inspirational: {},
      professional: {},
      raw: {},
      scholarly: {},
      sexy: {},
      silly: {},
      soothing: {},
      spooky: {},
      surreal: {},
      young: {},
      woo_woo: {},
    },
  },
} as const

export type FeatureDetail =
  | {
      /**
       * defaults to proper case of feature
       */
      title?: string
      /**
       * Image for results page
       */
      image?: string
      header?: string
      body?: string
      note?: string
      examples?: readonly string[]
      type?: string
      /**
       * Optional feature (e.g. fiction_nonfiction>fiction) that is required to show this
       */
      requires?: string
    }
  | undefined

// CATEGORIES
export const CATEGORIES = Object.keys(category_info)
/**
 * Highest level organization. combines to make a category_feature: {category}>{feature}
 */
export type Category = typeof CATEGORIES[number]

/**
 * [ category>feature1, category>feature2, ... ]
 */
export const CATEGORY_FEATURES = Object.entries(category_info).reduce(
  (accu, [category, category_info]) => [
    ...accu,
    ...Object.keys(category_info.features).map(
      (feature) => `${category}>${feature}`,
    ),
  ],
  [] as string[],
)
// console.log(CATEGORY_FEATURES)

/**
 * Full form of {category}>{feature}
 */
export type CategoryFeature = typeof CATEGORY_FEATURES[number]

export const FEATURES = Object.entries(category_info).reduce(
  (accu, [category, category_info]) => [
    ...accu,
    ...Object.keys(category_info.features),
  ],
  [] as string[],
)

/**
 * Feature by itself. Partial form of {category}>{feature}
 */
export type Feature = typeof FEATURES[number]

/**
 * { [category>feature]: number }
 */
export type CategoryFeatureDictType = { [key in CategoryFeature]?: number }

/**
 * { [feature]: number }
 */
export type FeatureDictType = { [key in Feature]?: number }

/**
 * { [category]: {[category>feature]: number} }
 */
export type CategoryToCategoryFeatureDictType = {
  [key in Category]: CategoryFeatureDictType
}

/**
 * { [category]: {[feature]: number} }
 */
export type CategoryToFeatureDictType = {
  [key in Category]: FeatureDictType
}
